import { BREAKPOINTS } from "../../config/breakpoints";
import React, { useState } from "react";
import { Container, Divider, InputContain, IRow, Span } from "../GodMode";
import Button from "../../Generic/Button";
import NumberListComponent from "../../Generic/NumberListSelect";
import Checkbox from "../../Generic/Checkbox";
import Card from "../../Generic/Card";
import InputBox from "../../Generic/InputBox";
import { errorNotification } from "../../Generic/Notifications";
import styled from "styled-components";

const CHEF_TASKS = {
	TURN_NO_MACHINE: "TURN_ON_MACHINE",
	TURN_OFF_MACHINE: "TURN_OFF_MACHINE",
	BOARDS_OTA: "OTA",
	READY_MACHINE : "READY_MACHINE",
	TURN_ON_DOCKING_LIGHT : "TURN_ON_DOCKING_LIGHT",
	TURN_OFF_DOCKING_LIGHT: "TURN_OFF_DOCKING_LIGHT",
	UPLOAD_OFFLINE_TEMPERATURE_DATA: "UPLOAD_OFFLINE_TEMPERATURE_DATA",
};

const CHEF_TASKS_CONFIG = {
	[CHEF_TASKS.TURN_NO_MACHINE]: { 
		taskType: "turn_on_machine", 
		name: "Turn Machine ON" 
	},
	[CHEF_TASKS.TURN_OFF_MACHINE]: { 
		taskType: "turn_off_machine", 
		name: "Turn Machine OFF" 
	},
	[CHEF_TASKS.BOARDS_OTA]: { 
		taskType: "ota", 
		name: "OTA Update" 
	},
	[CHEF_TASKS.READY_MACHINE]: { 
		taskType: "ready_machine", 
		name: "Ready Machine" 
	},
	[CHEF_TASKS.TURN_ON_DOCKING_LIGHT]: { 
		taskType: "turn_on_lights", 
		name: "Turn ON Lights" 
	},
	[CHEF_TASKS.TURN_OFF_DOCKING_LIGHT]: { 
		taskType: "turn_off_lights", 
		name: "Turn OFF Lights" 
	},
	[CHEF_TASKS.UPLOAD_OFFLINE_TEMPERATURE_DATA]: {
		taskType: "upload_temperature_data_saved_offline",
		name: "Upload Offline Temperature Data",
	},
};

function getLocalDateInputValue(date = new Date()) {
	const yyyy = date.getFullYear();
	const mm = String(date.getMonth() + 1).padStart(2, "0");
	const dd = String(date.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}

function toChefDateFormat(isoDate) {
	const [year, month, day] = isoDate.split("-");
	if (!year || !month || !day) {
		return null;
	}
	return `${day}-${month}-${year}`;
}

const CheckboxRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	width: 100%;
`;

const CardDivider = styled.div`
	border-top: 1px solid rgba(0, 0, 0, 0.15);
	width: 100%;
`;

const InputAndButtonRow = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 1rem;
	width: 100%;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		flex-direction: column;
		gap: 0.75rem;
	}
`;

const InputSection = styled.div`
	flex: 1;
	width: 100%;
`;

const OtaButtonWrapper = styled.div`
	display: flex;
	align-items: center;
	padding-top: 0.25rem;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		width: 100%;
		padding-top: 0;
		& > button {
			width: 100%;
		}
	}
`;

const Chef = ({ sendTask, loadingStates }) => {
	const [modulesToUpdate, setModulesToUpdate] = useState([]);
	const [checkForUpdates, setCheckForUpdates] = useState(true);
	const [offlineTemperatureDate, setOfflineTemperatureDate] = useState(getLocalDateInputValue());

	const isTurnOnLoading = loadingStates[CHEF_TASKS_CONFIG[CHEF_TASKS.TURN_NO_MACHINE].taskType] || false;
	const isTurnOffLoading = loadingStates[CHEF_TASKS_CONFIG[CHEF_TASKS.TURN_OFF_MACHINE].taskType] || false;
	const isReadyLoading = loadingStates[CHEF_TASKS_CONFIG[CHEF_TASKS.READY_MACHINE].taskType] || false;
	const isOTALoading = loadingStates[CHEF_TASKS_CONFIG[CHEF_TASKS.BOARDS_OTA].taskType] || false;
	const isDockingLightOnLoading = loadingStates[CHEF_TASKS_CONFIG[CHEF_TASKS.TURN_ON_DOCKING_LIGHT].taskType] || false;
	const isDockingLightOffLoading = loadingStates[CHEF_TASKS_CONFIG[CHEF_TASKS.TURN_OFF_DOCKING_LIGHT].taskType] || false;
	const isUploadOfflineTemperatureLoading = loadingStates[CHEF_TASKS_CONFIG[CHEF_TASKS.UPLOAD_OFFLINE_TEMPERATURE_DATA].taskType] || false;

	function prepareModulePayload(moduleList) {
		let newPayload = [];
		for(const moduleId of moduleList) {
			newPayload.push({ moduleId });
		}
		return newPayload;
	}

	function sendOTATask(modulesToUpdate, checkForUpdates) {
		const modulePayload = prepareModulePayload(modulesToUpdate);
		const successCallback = () => {
			setModulesToUpdate([]);
		};
		sendTask(CHEF_TASKS_CONFIG[CHEF_TASKS.BOARDS_OTA], { checkForUpdates, modulesToUpdate: modulePayload }, successCallback);
	}

	const handleTurnOnMachine = async () => {
		await sendTask(CHEF_TASKS_CONFIG[CHEF_TASKS.TURN_NO_MACHINE], {});
	};

	const handleTurnOffMachine = async () => {
		await sendTask(CHEF_TASKS_CONFIG[CHEF_TASKS.TURN_OFF_MACHINE], {});
	};

	const handleReadyMachine = async () => {
		await sendTask(CHEF_TASKS_CONFIG[CHEF_TASKS.READY_MACHINE], {});
	};

	const handleTurnOnDockingLight = async () => {
		await sendTask(CHEF_TASKS_CONFIG[CHEF_TASKS.TURN_ON_DOCKING_LIGHT], {});
	}

	const handleTurnOffDockingLight = async () => {
		await sendTask(CHEF_TASKS_CONFIG[CHEF_TASKS.TURN_OFF_DOCKING_LIGHT], {});
	}

	const handleUploadOfflineTemperatureData = async () => {
		const date = toChefDateFormat(offlineTemperatureDate);
		if (!date) {
			errorNotification("Select a date to fetch offline temperature data");
			return;
		}
		await sendTask(CHEF_TASKS_CONFIG[CHEF_TASKS.UPLOAD_OFFLINE_TEMPERATURE_DATA], { date });
	};

	return (
		<>
			<Container>
				<Button onClick={handleTurnOnMachine} disabled={isTurnOnLoading}>
					Turn Machine ON
				</Button>
				<Button onClick={handleTurnOffMachine} disabled={isTurnOffLoading}>
					Turn Machine OFF
				</Button>
				<Button onClick={handleReadyMachine} disabled={isReadyLoading}>
					Ready Machine
				</Button>
			</Container>
			<Divider />
			<Container>
				<Card
					width="100%"
					padding="1.5rem"
					gap="1.25rem"
					boxShadow="0px 1px 17px -8px rgba(0, 0, 0, 1)"
				>
					<CheckboxRow>
						<Checkbox defaultChecked={checkForUpdates} onClick={(e) => setCheckForUpdates(e.target.checked)} />
						<span style={{ fontSize: '1rem', fontWeight: 500 }}>Check For Updates</span>
					</CheckboxRow>
					<CardDivider />
					<InputAndButtonRow>
						<InputSection>
							<NumberListComponent placeholder={"Enter Module to Update"} numbers={modulesToUpdate} setNumbers={setModulesToUpdate} />
						</InputSection>
						<OtaButtonWrapper>
							<Button
								onClick={() => sendOTATask(modulesToUpdate, checkForUpdates)}
								disabled={isOTALoading}>
								OTA
							</Button>
						</OtaButtonWrapper>
					</InputAndButtonRow>
				</Card>
			</Container>
			<Divider />
			<Container>
				<Button onClick={handleTurnOnDockingLight} disabled={isDockingLightOnLoading}>
					Turn ON Docking Light
				</Button>
				<Button onClick={handleTurnOffDockingLight} disabled={isDockingLightOffLoading}>
					Turn OFF Docking Light
				</Button>
			</Container>
			<Divider />
			<Container>
				<IRow>
					<InputContain margin="0 1rem 0 0">
						<Span padding="0 0 0.5rem 0" fontSize="1rem" fontWeight="600">
							Offline Temperature Date
						</Span>
						<InputBox
							value={offlineTemperatureDate}
							disabled={isUploadOfflineTemperatureLoading}
							onChange={(e) => setOfflineTemperatureDate(e.target.value)}
							type="date"
							required
						/>
					</InputContain>
					<Button onClick={handleUploadOfflineTemperatureData} disabled={isUploadOfflineTemperatureLoading}>
						Upload Offline Temperature Data
					</Button>
				</IRow>
			</Container>
		</>
	);
};

export default Chef;
